import React, { useEffect, useState, useRef } from 'react';
import projectAPI from '../api/projectAPI';
import UserDashboard from './UserDashboard';
import {
  FaUserPlus, FaTimes, FaLink, FaChevronRight,
  FaCalendarAlt, FaAlignLeft,
  FaUpload, FaFilePdf, FaFileWord, FaFileImage,
  FaFile, FaDownload, FaEye, FaSpinner, FaTrash,
  FaFileAlt,
} from 'react-icons/fa';
import { MdAssignment, MdGroups } from 'react-icons/md';
import Webcam from 'react-webcam';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>{role}</span>
);
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-indigo-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── File helpers ──────────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const fmtSize = (b) => b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

// ── FileUploadPanel ───────────────────────────────────────────────────────────
const FileUploadPanel = ({ projectId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploaded, setUploaded]     = useState(existingFiles);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fileList) => setPending(prev => [...prev, ...Array.from(fileList).map(f => ({
    file: f, name: f.name, size: f.size, fileType: getFileType(f),
    preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
  }))]);

  const handleUpload = async () => {
    if (!projectId) { alert('Save the project first.'); return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(pending.map(async pf => ({ base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType })));
      const res = await projectAPI.post(`/${projectId}/attachments`, { files });
      setUploaded(res.data.attachments); setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch { alert('❌ Upload failed.'); } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await projectAPI.delete(`/${projectId}/attachments/${id}`); setUploaded(prev => prev.filter(f => f._id !== id)); }
    catch { alert('❌ Delete failed.'); }
  };

  return (
    <div className="border border-indigo-200 rounded-xl p-4 bg-indigo-50 space-y-3 mt-4">
      <h4 className="font-semibold text-indigo-700 text-sm flex items-center gap-2">
        <FaUpload /> Attachments <span className="text-xs font-normal text-gray-500">(visible to all assignees)</span>
      </h4>
      <div onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-indigo-300 rounded-lg p-4 text-center cursor-pointer hover:bg-indigo-100 transition">
        <FaUpload className="mx-auto text-indigo-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p><p className="text-xs text-gray-400">{fmtSize(pf.size)}</p></div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{f.name}</p><p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p></div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!uploaded.length && !pending.length && <p className="text-xs text-gray-400 text-center">No attachments yet.</p>}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PLProjectManager — Project Leads creates & assigns to TL + Developer
// Also includes Submit Report (PL privilege)
// ─────────────────────────────────────────────────────────────────────────────
const PLProjectManager = () => {
  const worker = JSON.parse(localStorage.getItem('worker'));

  const [assignableWorkers, setAssignableWorkers]   = useState([]);
  const [myProjects, setMyProjects]                 = useState([]);
  const [selectedIds, setSelectedIds]               = useState([]);
  const [assigneeDropOpen, setAssigneeDropOpen]     = useState(false);
  const [savedProjectId, setSavedProjectId]         = useState(null);
  const [savedProjectTitle, setSavedProjectTitle]   = useState('');
  const [showAttachModal, setShowAttachModal]       = useState(false);
  const [attachProject, setAttachProject]           = useState(null);
  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardProjectId, setForwardProjectId]     = useState(null);
  const [forwardProjectTitle, setForwardProjectTitle] = useState('');
  const [forwardTargets, setForwardTargets]         = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);

  // Report modal
  const [showReportModal, setShowReportModal]     = useState(false);
  const [reportProject, setReportProject]         = useState(null);
  const [reportDescription, setReportDescription] = useState('');
  const [reportPhoto, setReportPhoto]             = useState(null);
  const [submittingReport, setSubmittingReport]   = useState(false);
  const webcamRef = useRef(null);

  // View reports modal
  const [showViewReports, setShowViewReports]       = useState(false);
  const [viewReportsProject, setViewReportsProject] = useState(null);
  const [reportsList, setReportsList]               = useState([]);

  const [formData, setFormData] = useState({ title: '', description: '', startDate: '', endDate: '' });

  useEffect(() => { fetchAssignable(); fetchMyProjects(); }, []);

  const fetchAssignable = async () => {
    try { const res = await projectAPI.get('/assignable-workers'); setAssignableWorkers(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchMyProjects = async () => {
    try { const res = await projectAPI.get('/created-by-me'); setMyProjects(res.data); }
    catch (err) { console.error(err); }
  };

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) { alert('Fill all required fields.'); return; }
    if (!selectedIds.length) { alert('Select at least one assignee.'); return; }
    try {
      const res = await projectAPI.post('/chain', { ...formData, assigneeIds: selectedIds });
      setSavedProjectId(res.data._id); setSavedProjectTitle(res.data.title);
      alert('✅ Project assigned! You can now upload attachments below.');
      setFormData({ title: '', description: '', startDate: '', endDate: '' });
      setSelectedIds([]); setAssigneeDropOpen(false);
      fetchMyProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Failed to create project.'); }
  };

  const openForward = async (project) => {
    setForwardProjectId(project._id); setForwardProjectTitle(project.title); setForwardSelectedIds([]);
    try { const res = await projectAPI.get('/assignable-workers'); setForwardTargets(res.data); }
    catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await projectAPI.post(`/${forwardProjectId}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Forwarded!'); setShowForwardModal(false); fetchMyProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  const openReportModal = (project) => {
    setReportProject(project); setReportDescription(''); setReportPhoto(null); setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportDescription || !reportPhoto) { alert('Description & Photo required.'); return; }
    setSubmittingReport(true);
    try {
      await projectAPI.post(`/${reportProject._id}/report`, { description: reportDescription, photo: reportPhoto });
      alert('✅ Report submitted!'); setShowReportModal(false); fetchMyProjects();
    } catch (err) { alert(err.response?.data?.error || '❌ Failed to submit report.'); }
    finally { setSubmittingReport(false); }
  };

  const openViewReports = async (project) => {
    setViewReportsProject(project); setReportsList([]);
    try { const res = await projectAPI.get(`/${project._id}/reports`); setReportsList(res.data.reports || []); }
    catch (err) { console.error(err); }
    setShowViewReports(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-6 pt-20">
      <UserDashboard />
      <h2 className="text-3xl font-bold font-mono text-indigo-700 mb-2 text-center mt-3">
        Project Leads — Project Manager
      </h2>
      <p className="text-center text-sm text-gray-500 mb-6">
        Assign projects to{' '}
        <span className="font-semibold text-blue-600">TL</span> and{' '}
        <span className="font-semibold text-green-600">Developers</span>
      </p>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── CREATE FORM ── */}
        <div className="bg-white rounded-xl shadow-lg p-6 overflow-y-auto max-h-[85vh]">
          <h3 className="text-xl font-semibold text-indigo-600 flex items-center gap-2 mb-4">
            <MdAssignment /> Create & Assign Project
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Project Title</label>
              <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                placeholder="Enter project title" required
                className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-400 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1"><FaAlignLeft className="inline mr-1" /> Description</label>
              <textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the project..." required
                className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-400 outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1"><FaCalendarAlt className="inline mr-1" /> Start Date</label>
                <input type="date" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})}
                  className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-400 outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1"><FaCalendarAlt className="inline mr-1" /> End Date</label>
                <input type="date" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})}
                  className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-400 outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                <MdGroups className="inline mr-1" /> Assign To — TL or Developer
              </label>
              <button type="button" onClick={() => setAssigneeDropOpen(!assigneeDropOpen)}
                className="border px-4 py-2 rounded-lg w-full text-left bg-white text-sm">
                {selectedIds.length === 0 ? 'Select workers...' : `${selectedIds.length} worker(s) selected`}
              </button>
              {assigneeDropOpen && (
                <div className="mt-1 border rounded-lg bg-white shadow z-20 max-h-48 overflow-y-auto">
                  {assignableWorkers.length === 0 && <p className="text-sm text-gray-400 p-3">No assignable workers found.</p>}
                  {assignableWorkers.map(w => (
                    <label key={w._id}
                      className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-indigo-50 text-sm ${selectedIds.includes(w._id) ? 'bg-indigo-50' : ''}`}>
                      <input type="checkbox" checked={selectedIds.includes(w._id)}
                        onChange={() => toggleSelect(w._id)} className="accent-indigo-600" />
                      <span className="flex-1 font-medium text-gray-700">{w.username}</span>
                      <RoleBadge role={w.role} />
                      {w.team && <span className="text-xs text-gray-400">{w.team}</span>}
                    </label>
                  ))}
                </div>
              )}
              {selectedIds.length > 0 && <p className="text-xs text-indigo-600 mt-1">{selectedIds.length} selected</p>}
            </div>
            <button type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg transition">
              Assign Project
            </button>
          </form>

          {savedProjectId && (
            <div className="mt-3">
              <p className="text-xs text-green-600 font-medium mb-1">✅ Files for: <span className="font-bold">{savedProjectTitle}</span></p>
              <FileUploadPanel projectId={savedProjectId}
                existingFiles={myProjects.find(p => p._id === savedProjectId)?.attachments || []}
                onUploaded={(attachments) => setMyProjects(prev => prev.map(p => p._id === savedProjectId ? { ...p, attachments } : p))} />
            </div>
          )}
        </div>

        {/* ── PROJECT LIST ── */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-indigo-600 mb-4 flex items-center gap-2">
            <FaLink /> Projects I Created
          </h3>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
            {myProjects.length === 0 && <p className="text-center text-gray-400 py-8">No projects created yet.</p>}
            {myProjects.map(project => (
              <div key={project._id} className="border border-indigo-100 rounded-xl p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <h4 className="font-semibold text-gray-800">{project.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{project.description}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
                    project.status === 'Completed' ? 'bg-green-100 text-green-700'
                    : project.status === 'On Hold'  ? 'bg-blue-100 text-blue-700'
                    : 'bg-yellow-100 text-yellow-700'}`}>{project.status}</span>
                </div>
                <div className="flex gap-4 text-xs text-gray-400 mt-1">
                  {project.startDate && <span>Start: {project.startDate.slice(0,10)}</span>}
                  {project.endDate   && <span>End: {project.endDate.slice(0,10)}</span>}
                </div>
                {project.assignmentChain?.length > 0 && (
                  <div className="mt-2"><p className="text-xs text-gray-400 mb-1">Chain:</p><ChainTrail chain={project.assignmentChain} /></div>
                )}
                {project.assignees?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">Assigned to:</p>
                    <div className="flex flex-wrap gap-2">
                      {project.assignees.map((a, i) => (
                        <div key={i} className="flex items-center gap-1 bg-gray-50 border rounded-full px-3 py-1">
                          <span className="text-xs font-medium text-gray-700">{a.username}</span>
                          <RoleBadge role={a.role} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {project.status !== 'Completed' && (
                    <button onClick={() => openForward(project)}
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                      <FaUserPlus /> Forward
                    </button>
                  )}
                  <button onClick={() => { setAttachProject(project); setShowAttachModal(true); }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                    <FaUpload /> {project.attachments?.length > 0 ? `${project.attachments.length} file(s)` : 'Add files'}
                  </button>
                  {/* Submit Report — PL only */}
                  <button onClick={() => openReportModal(project)}
                    className="flex items-center gap-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-1.5">
                    <FaFileAlt /> Submit Report
                  </button>
                  {project.reports?.length > 0 && (
                    <button onClick={() => openViewReports(project)}
                      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 rounded-lg px-3 py-1.5">
                      <FaFileAlt /> Reports ({project.reports.length})
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attachment modal */}
      {showAttachModal && attachProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-indigo-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{attachProject.title}</span>"</p>
            <FileUploadPanel projectId={attachProject._id} existingFiles={attachProject.attachments || []}
              onUploaded={(attachments) => {
                setMyProjects(prev => prev.map(p => p._id === attachProject._id ? { ...p, attachments } : p));
                setAttachProject(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* Report submit modal */}
      {showReportModal && reportProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowReportModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-xl font-bold text-indigo-700 mb-1 text-center">Project Report</h3>
            <p className="text-sm text-gray-500 text-center mb-4">"{reportProject.title}"</p>
            <textarea placeholder="Describe project progress..." value={reportDescription}
              onChange={e => setReportDescription(e.target.value)}
              className="border rounded-lg w-full p-3 mb-4 focus:ring-2 focus:ring-indigo-400 outline-none" rows="4" />
            <div className="rounded-lg overflow-hidden mb-3 border">
              <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="w-full" videoConstraints={{ facingMode: 'environment' }} />
            </div>
            <button onClick={() => setReportPhoto(webcamRef.current.getScreenshot())}
              className="w-full bg-blue-600 text-white py-2 rounded-lg mb-3 hover:bg-blue-700">Capture Photo</button>
            {reportPhoto && <img src={reportPhoto} alt="preview" className="w-full rounded-lg mb-3 border" />}
            <button onClick={submitReport} disabled={submittingReport}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 rounded-lg flex items-center justify-center gap-2">
              {submittingReport ? <><FaSpinner className="animate-spin" /> Submitting...</> : 'Submit Report'}
            </button>
          </div>
        </div>
      )}

      {/* View reports modal */}
      {showViewReports && viewReportsProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowViewReports(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-indigo-700 mb-1">Project Reports</h3>
            <p className="text-sm text-gray-500 mb-4">"{viewReportsProject.title}"</p>
            {reportsList.length === 0
              ? <p className="text-center text-gray-400 py-8">No reports submitted yet.</p>
              : <div className="space-y-4">
                  {reportsList.map((r, i) => (
                    <div key={i} className="border rounded-xl p-4 bg-indigo-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-semibold text-indigo-700">{r.submittedBy}</span>
                        <span className="text-xs text-gray-400">{new Date(r.submittedAt).toLocaleDateString('en-GB')}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-3">{r.description}</p>
                      {r.photo && <img src={r.photo} alt="report" className="w-full rounded-lg border" />}
                    </div>
                  ))}
                </div>}
          </div>
        </div>
      )}

      {/* Forward modal */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-indigo-700 mb-1">Forward Project</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{forwardProjectTitle}</span>"</p>
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto mb-4">
              {forwardTargets.length === 0 ? <p className="text-sm text-gray-400 p-3 text-center">No eligible workers.</p>
                : forwardTargets.map(w => (
                  <label key={w._id}
                    className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-indigo-50 ${forwardSelectedIds.includes(w._id) ? 'bg-indigo-50' : ''}`}>
                    <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                      onChange={() => setForwardSelectedIds(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                      className="accent-indigo-600" />
                    <span className="flex-1 text-sm font-medium text-gray-700">{w.username}</span>
                    <RoleBadge role={w.role} />
                  </label>
                ))}
            </div>
            {forwardSelectedIds.length > 0 && <p className="text-xs text-indigo-600 mb-3">{forwardSelectedIds.length} selected</p>}
            <button onClick={handleForward} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg">Forward Project</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PLProjectManager;